import Decimal from 'decimal.js'
import {
    DiscountContract,
    VoidCompReasonModel,
    DiscountType,
    DiscountGetDiscountType,
    DiscountLevel,
} from 'models'
import { OrderItemViewModel } from 'store/models/sellModels'

import { mockDiscountForComp } from 'views/Sale/discount/mockDiscountForComp'
import {
    adjustSplitedValue,
    calculateLineSubtotal,
    ceil,
    round,
} from './common'
import { sortBy } from 'lodash-es'
import { sumDecimal } from 'utilities/decimalsManipulate'
import { LineModelExtend } from './models/IOrderCalculationModel'
import { sortDiscountTypeOfDiscount } from 'views/Sale/discount/discountMapper/discountSortting'

export interface IDiscountResult {
    discount: DiscountContract
    amount: Decimal
}
export const calculateDiscounts = (
    maxSubtotal: Decimal,
    discounts: DiscountContract[],
    itemAmount: number = 1,
): IDiscountResult[] => {
    let totalDiscount = new Decimal(0)
    return discounts.map((d) => {
        const expectDiscount = calculateDiscountAmount(
            d,
            d.type === DiscountType.Percentage
                ? maxSubtotal
                : maxSubtotal.sub(totalDiscount),
            itemAmount,
        )
        const discountAmount = Decimal.min(maxSubtotal, expectDiscount)
        totalDiscount = totalDiscount.plus(discountAmount)
        return {
            discount: d,
            amount: discountAmount,
        }
    })
}

export const calculateTotalDiscountsAmount = (
    maxSubtotal: Decimal,
    discounts: DiscountContract[],
    itemAmount: number = 1,
): {
    totalDiscountAmount: Decimal
    discountResult: IDiscountResult[]
} => {
    if (maxSubtotal.lessThan(0))
        return {
            totalDiscountAmount: new Decimal(0),
            discountResult: discounts.map((a) => ({
                discount: a,
                amount: new Decimal(0),
            })),
        }

    const discountResults = calculateDiscounts(
        maxSubtotal,
        discounts,
        itemAmount,
    )
    const totalDiscount = sumDecimal(discountResults.map((a) => a.amount))

    return {
        totalDiscountAmount: Decimal.min(totalDiscount, maxSubtotal),
        discountResult: discountResults,
    }
}

/**
 * get item subtotal after discount
 * @param item OrderItemViewModel
 * @param discounts Item level DiscountContract
 * @returns
 */
export const calculateLineSubTotalAfterDiscounts = (
    item: OrderItemViewModel,
    discounts: DiscountContract[],
) => {
    const lineSubtotal = calculateLineSubtotal(item)

    const {
        totalDiscountAmount: discountAmount,
    } = calculateTotalDiscountsAmount(lineSubtotal, discounts, item.amount)
    return lineSubtotal.sub(discountAmount)
}
export const splitedOrderDiscountToLine = (
    lines: LineModelExtend[],
    maximumDiscount: Decimal,
    entireDiscounts: DiscountContract[],
    entireComp: VoidCompReasonModel | undefined,
    isCalculateCompAfterDiscount: boolean,
    sortLine: boolean = true,
) => {
    const sortedDiscounts = sortDiscountTypeOfDiscount(entireDiscounts)

    if (sortLine) {
        lines = sortBy(lines, [
            (l) =>
                (l.lineTotalWithItemDiscount ?? 0) - (l.totalCompDiscount ?? 0),
            (l) => l.sortingIndex,
        ])
        lines.reverse()
    }
    lines = lines.map((l, i) => ({ ...l, sortingIndex: i }))

    sortedDiscounts.forEach((discount) => {
        const splitedResults = distributeDiscountValueToLines(
            discount,
            maximumDiscount,
            lines,
            false,
        )
        splitedResults.forEach((r) => {
            const line = lines.find((a) => a.orderItemId === r.id)!
            line.orderDiscountAmount = line.orderDiscountAmount.plus(
                r.splitedResult,
            )
        })
    })

    const totalDiscountAmount = sumDecimal(
        lines.map((l) => l.orderDiscountAmount),
    )

    if (entireComp) {
        const splitedResults = distributeDiscountValueToLines(
            {
                level: DiscountLevel.EntireOrder,
                type: entireComp.isPercentage
                    ? DiscountType.Percentage
                    : DiscountType.FixedAmount,
                value: entireComp.value ?? 0,
            },
            isCalculateCompAfterDiscount
                ? maximumDiscount.sub(totalDiscountAmount)
                : maximumDiscount,
            lines,
            isCalculateCompAfterDiscount,
        )
        splitedResults.forEach((r) => {
            const line = lines.find((a) => a.orderItemId === r.id)!
            line.orderDiscountAmount = line.orderDiscountAmount.plus(
                r.splitedResult,
            )
        })
    }

    return lines
}

export function calculateTotalEntireDiscount(
    subtotal: Decimal,
    entireDiscounts: DiscountContract[],
    entireComp: VoidCompReasonModel | undefined,
    isCalculateCompAfterDiscount: boolean,
) {
    const {
        totalDiscountAmount: totalEntireDiscount,
    } = calculateTotalDiscountsAmount(subtotal, entireDiscounts)
    if (entireComp) {
        const maxTotal = isCalculateCompAfterDiscount
            ? Decimal.max(new Decimal(0), subtotal.sub(totalEntireDiscount))
            : subtotal
        const compDiscount = mockDiscountForComp(entireComp, true, maxTotal)
        return totalEntireDiscount.plus(compDiscount.value ?? 0)
    }
    return totalEntireDiscount
}
interface IDistributeLine {
    id: string
    subtotal: Decimal
    splitedResult: Decimal
    extraDiscount: Decimal
}
export function distributeDiscountValueToLines(
    discount: DiscountContract,
    maximumSumDiscount: Decimal,
    itemLines: LineModelExtend[],
    isCalculationAfterOrderDiscount: boolean,
): IDistributeLine[] {
    const splitedLines = itemLines.map<IDistributeLine>((a) => {
        return {
            id: a.orderItemId,
            subtotal: a.subtotal
                .sub(a.totalCompDiscount ?? 0)
                .sub(a.itemDiscountAmount ?? 0),
            extraDiscount: a.orderDiscountAmount,
            splitedResult: new Decimal(0),
        }
    })

    const expectedDiscountAmount = calculateDiscountAmount(
        discount,
        maximumSumDiscount,
    )

    const splitLineDiscounts = splitedLines.map((line) => {
        let lineDiscountAmount = new Decimal(0)
        if (discount.type === DiscountType.FixedAmount) {
            const orderLineRatio = line.subtotal.equals(0)
                ? new Decimal(0)
                : ceil(line.subtotal.div(maximumSumDiscount))
            lineDiscountAmount = expectedDiscountAmount.mul(orderLineRatio)
        } else {
            const lineNetSubtotal = line.subtotal.sub(
                isCalculationAfterOrderDiscount ? line.extraDiscount : 0,
            )
            lineDiscountAmount = calculateDiscountAmount(
                discount,
                lineNetSubtotal,
            )
        }
        return Decimal.min(
            line.subtotal.sub(line.extraDiscount),
            ceil(lineDiscountAmount),
        )
    })
    adjustSplitedValue(splitLineDiscounts, expectedDiscountAmount)
    splitedLines.forEach((l, index) => {
        l.splitedResult = l.splitedResult.plus(splitLineDiscounts[index])
    })
    return splitedLines
}
const calculateDiscountPercentageAmount = (
    percent: number,
    subtotal: Decimal,
) => {
    return round(subtotal.mul(percent).div(100))
}

export const calculateDiscountAmount = (
    discount: DiscountContract,
    /** line.price * amount */
    maxDiscountable: Decimal,
    /** require for order line case */
    itemAmount: number = 1,
) => {
    if (
        discount.type === DiscountType.BuyXGetY &&
        discount.getAtDiscountType === DiscountGetDiscountType.Free
    )
        return maxDiscountable
    let discountPrice = getDiscountValue(discount, maxDiscountable)
    if (
        discount.level === DiscountLevel.Item &&
        (discount.type === DiscountType.FixedAmount ||
            discount.getAtDiscountType === DiscountGetDiscountType.FixedAmount)
    ) {
        discountPrice = discountPrice.mul(itemAmount)
    }

    return Decimal.min(discountPrice, maxDiscountable)
}

const getDiscountValue = (discount: DiscountContract, subtotal: Decimal) => {
    return discount.type === DiscountType.Percentage ||
        discount.getAtDiscountType === DiscountGetDiscountType.Percentage
        ? calculateDiscountPercentageAmount(discount.value!!, subtotal)
        : new Decimal(discount?.value ?? 0)
}
